
// export default InventorySummaryPage;

import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInventoryReport, exportInventoryReport, clearError } from 'store/slices/reportSlice';
import { fetchInventoryCategories, fetchAllInventorySubcategories } from 'store/slices/commonSlice';
import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { AlertCircle, Package, TrendingDown, Archive, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import FormField from 'components/common/FormField';
import { formatDateTime } from 'utlis/formateDateTime';
import CategorySubcategorySelect from 'components/common/CategorySubcategorySelect';

const InventorySummaryPage = () => {
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);

  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    status: 'all',
    reorder_suggested: 'all',
    available_quantity_gte: '',
    available_quantity_lte: '',
    buffer_threshold_gte: '',
    buffer_threshold_lte: '',
    category: 'all',
    sub_category: 'all',
  });

  const {
    inventoryReport = {},
    isLoading,
    error,
  } = useSelector((state) => state.report || {});

  // Get categories from Redux state
  const categories = useSelector((state) => state.common?.inventoryCategories.items || []);

  const subcategories = useSelector((state) => state.common?.allInventorySubcategories.items || []);

  const summary = inventoryReport?.data?.summary || {};
  const results = inventoryReport?.data?.results || [];
  const pagination = inventoryReport?.pagination || {};

  // Calculate total pages from server response
  const totalPages = pagination.total_pages || 1;

  // Check if any advanced filters are active (excluding 'all' values)
  const hasActiveAdvancedFilters = Object.values(advancedFilters).some(value =>
    value !== '' && value !== 'all'
  );

  // --- Effects ---
  const refetch = useCallback(() => {
    // Build the API params object with all filters
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      ...dateFilters,
      // Add advanced filters with proper naming for the backend
      ...(advancedFilters.status !== 'all' && { status: advancedFilters.status }),
      ...(advancedFilters.reorder_suggested !== 'all' && { reorder_suggested: advancedFilters.reorder_suggested }),
      ...(advancedFilters.available_quantity_gte && { available_quantity__gte: advancedFilters.available_quantity_gte }),
      ...(advancedFilters.available_quantity_lte && { available_quantity__lte: advancedFilters.available_quantity_lte }),
      ...(advancedFilters.buffer_threshold_gte && { buffer_threshold__gte: advancedFilters.buffer_threshold_gte }),
      ...(advancedFilters.buffer_threshold_lte && { buffer_threshold__lte: advancedFilters.buffer_threshold_lte }),
      ...(advancedFilters.category !== 'all' && { category: advancedFilters.category }),
      ...(advancedFilters.sub_category !== 'all' && { sub_category: advancedFilters.sub_category }),
    };

    dispatch(fetchInventoryReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);


  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  useEffect(() => {
    dispatch(fetchInventoryCategories());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchAllInventorySubcategories(
        advancedFilters.category !== "all"
          ? advancedFilters.category
          : null
      )
    );
  }, [advancedFilters.category, dispatch]);

  // --- Event Handlers ---
  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      status: 'all',
      reorder_suggested: 'all',
      available_quantity_gte: '',
      available_quantity_lte: '',
      buffer_threshold_gte: '',
      buffer_threshold_lte: '',
      category: 'all',
      sub_category: 'all',
    });
  };

  const handleCategoryChange = (value) => {
    handleAdvancedFilterChange('category', value);
    handleAdvancedFilterChange('sub_category', 'all'); // Reset subcategory when category changes
  };

  // --- Column Definitions ---
  const columns = [
    { accessorKey: 'Category', header: 'Category' },
    { accessorKey: 'Sub Category', header: 'Sub Category' },
    { accessorKey: 'Available Quantity', header: 'Available Quantity' },
    { accessorKey: 'Buffer Threshold', header: 'Buffer Threshold' },
    {
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.Status;
        let variant = 'default';
        if (status === 'Low Stock') variant = 'secondary';
        if (status === 'Out of Stock') variant = 'destructive';
        return <Badge variant={variant}>{status}</Badge>;
      },
    },
    {
      header: 'Reorder Suggested',
      cell: ({ row }) => {
        const suggested = row.original['Reorder Suggested'];
        return <Badge variant={suggested === 'Yes' ? 'destructive' : 'secondary'}>{suggested}</Badge>;
      },
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated At',
      cell: ({ row }) => <span className="text-sm">{formatDateTime(row.original.updated_at) ?? '-'}</span>,
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Inventory Summary Report"
        description="Overview of all inventory items, stock levels, and reorder suggestions"
      />

      <LogDateFilter onFilter={(f) => setDateFilters(f)} />

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
              >
                {isAdvancedFilterExpanded ? (
                  <>
                    Hide <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormField
                label="Status"
                name="status"
                type="select"
                value={advancedFilters.status}
                onChange={(e) =>
                  handleAdvancedFilterChange('status', e.target.value)
                }
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'Normal', label: 'Normal' },
                  { value: 'Low Stock', label: 'Low Stock' },
                  { value: 'Out of Stock', label: 'Out of Stock' },
                ]}
              />
              <FormField
                label="Reorder Suggested"
                name="reorder_suggested"
                type="select"
                value={advancedFilters.reorder_suggested}
                onChange={(e) =>
                  handleAdvancedFilterChange('reorder_suggested', e.target.value)
                }
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'Yes', label: 'Yes' },
                  { value: 'No', label: 'No' },
                ]}
              />

              {/* <FormField
                label="Category"
                name="category"
                type="select"
                value={advancedFilters.category}
                onChange={(e) => handleCategoryChange(e.target.value)}
                options={[
                  { value: 'all', label: 'All' },
                  ...(Array.isArray(categories)
                    ? categories.map(category => ({
                      value: String(category.id),
                      label: category.name,
                    }))
                    : []),
                ]}
              />

              <FormField
                label="Sub Category"
                name="sub_category"
                type="select"
                value={advancedFilters.sub_category}
                onChange={(e) =>
                  handleAdvancedFilterChange('sub_category', e.target.value)
                }
                options={[
                  ...(Array.isArray(subcategories)
                    ? subcategories.map(subcategory => ({
                      value: String(subcategory.id),
                      label: subcategory.name,
                    }))
                    : []),
                ]}
              /> */}

              <CategorySubcategorySelect
                category={advancedFilters.category}
                subCategory={advancedFilters.sub_category}
                onCategoryChange={(value) => {
                  setAdvancedFilters((prev) => ({
                    ...prev,
                    category: value,
                    sub_category: "all",
                  }));
                }}
                onSubCategoryChange={(value) =>
                  setAdvancedFilters((prev) => ({
                    ...prev,
                    sub_category: value,
                  }))
                }
              />




              <div className="space-y-2">
                <Label>Available Quantity Range</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.available_quantity_gte}
                    onChange={(e) => handleAdvancedFilterChange('available_quantity_gte', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.available_quantity_lte}
                    onChange={(e) => handleAdvancedFilterChange('available_quantity_lte', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Buffer Threshold Range</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.buffer_threshold_gte}
                    onChange={(e) => handleAdvancedFilterChange('buffer_threshold_gte', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.buffer_threshold_lte}
                    onChange={(e) => handleAdvancedFilterChange('buffer_threshold_lte', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Sub-Categories</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary['Total Sub-Categories'] || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Available Quantity</CardTitle>
            <Archive className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{summary['Total Available Quantity'] || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Low Stock Items</CardTitle>
            <TrendingDown className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{summary['Low Stock Items'] || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Out of Stock Items</CardTitle>
            <AlertCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{summary['Out of Stock Items'] || 0}</div>
          </CardContent>
        </Card>
      </div>

      <DataTable
        data={results}
        columns={columns}
        totalCount={pagination.count}
        searchPlaceholder="Search by category, sub-category..."
        onSearch={setSearchTerm}
        showSearch={true}
        pageIndex={page - 1}
        pageSize={pageSize}
        pageCount={totalPages}
        onPageChange={(p) => setPage(p + 1)}
        onPageSizeChange={(newSize) => setPageSize(newSize)}
        isLoading={isLoading}
        onExport={() => dispatch(exportInventoryReport({
          filters: {
            ...dateFilters,
            search: searchTerm,
            // Include advanced filters in export
            ...(advancedFilters.status !== 'all' && { status: advancedFilters.status }),
            ...(advancedFilters.reorder_suggested !== 'all' && { reorder_suggested: advancedFilters.reorder_suggested }),
            ...(advancedFilters.available_quantity_gte && { available_quantity__gte: advancedFilters.available_quantity_gte }),
            ...(advancedFilters.available_quantity_lte && { available_quantity__lte: advancedFilters.available_quantity_lte }),
            ...(advancedFilters.buffer_threshold_gte && { buffer_threshold__gte: advancedFilters.buffer_threshold_gte }),
            ...(advancedFilters.buffer_threshold_lte && { buffer_threshold__lte: advancedFilters.buffer_threshold_lte }),
            ...(advancedFilters.category !== 'all' && { category: advancedFilters.category }),
            ...(advancedFilters.sub_category !== 'all' && { sub_category: advancedFilters.sub_category }),
          }
        }))}
        showExport={true}
        showImport={false}
      />
    </div>
  );
};

export default InventorySummaryPage;